import { now, offsetDateTimeToMs } from "@freightsimple/time/helpers";
import { useCallback, useEffect } from "react";
import { useCookies } from "react-cookie";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthorizationApi2 } from "@/Apis/AuthorizationApi2";
import { apiServerUrl, cookieDomain } from "@/Environment";
import { onUnauthorizedResponse } from "@/Helpers/unauthorizedResponseMiddleware";
import { resetPostHogUser } from "@/Integrations/resetPostHogUser";
import {
  SESSION_EXPIRY_BUFFER_MS,
  adoptStoredAuthentication,
  expireSession,
  isAuthenticationDisabled,
  readStoredAuthentication,
  useAuthentication,
} from "@/Slices/authenticationSlice";
import type { RootState } from "@/store";

// The longest delay setTimeout honours; past it the browser fires at once.
const MAX_TIMEOUT_MS = 2_147_483_647;

async function serverRejectsToken(accessToken: string): Promise<boolean> {
  try {
    const status = await new AuthorizationApi2(
      apiServerUrl(),
      accessToken,
    ).postAuthorizationVerifyToken();
    return status === 401;
  } catch (e) {
    // Being offline says nothing about the session.
    console.warn("Could not verify the session", { e });
    return false;
  }
}

/**
 * Ends the session the moment it is over: when the token expires, and when the
 * server rejects it. Every screen and poll that needs a session unmounts with
 * it, and the customer lands on sign-in with a way back to this page.
 *
 * Every tab shares one stored session but keeps its own copy in memory. A tab
 * whose copy is stale (another tab signed out or in since) follows the stored
 * session instead of ending it.
 *
 * Effects re-subscribe whenever the token changes. A tab that signs in again,
 * or signs in after loading signed out, is watched with its current token.
 */
export function useSessionExpiry() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [, , removeCookie] = useCookies(["fsSignedIn"]);
  const { isAuthenticated, refreshAuthentication, user } = useAuthentication();
  const userId = user?.userId;
  const accessToken = useSelector(
    (state: RootState) => state.authentication.accessToken,
  );
  const expiresAt = useSelector(
    (state: RootState) => state.authentication.expiresAt,
  );
  const watchedToken =
    !isAuthenticationDisabled() && isAuthenticated ? accessToken : undefined;

  const endSession = useCallback(
    function () {
      const stored = readStoredAuthentication();
      // Another tab ending this same session first leaves no token behind,
      // but this tab still keeps its own page to come back to.
      const sameSessionExpiredElsewhere =
        stored.accessToken === undefined &&
        stored.sessionExpired === true &&
        stored.redirectUserId === userId;
      if (stored.accessToken !== watchedToken && !sameSessionExpiredElsewhere) {
        // Ending it here would overwrite what the other tab stored, and leave
        // a way back after a deliberate Sign Out.
        dispatch(adoptStoredAuthentication(stored));
        return;
      }
      // The same cleanup Sign Out does: the marketing site reads the cookie to
      // show the visitor as signed in.
      removeCookie("fsSignedIn", { domain: cookieDomain() });
      resetPostHogUser();
      dispatch(expireSession({ redirectUrl: `${pathname}${search}`, userId }));
      navigate("/sign-in", { replace: true });
    },
    [dispatch, navigate, pathname, search, removeCookie, watchedToken, userId],
  );

  useEffect(
    function () {
      if (watchedToken === undefined || expiresAt === undefined) {
        return;
      }
      const delayMs =
        expiresAt - SESSION_EXPIRY_BUFFER_MS - offsetDateTimeToMs(now());
      // The 401 check below still ends a session this far out.
      if (delayMs > MAX_TIMEOUT_MS) {
        return;
      }
      const timeoutId = setTimeout(endSession, delayMs);
      return () => clearTimeout(timeoutId);
    },
    [watchedToken, expiresAt, endSession],
  );

  useEffect(
    function () {
      if (watchedToken === undefined) {
        return;
      }
      let unsubscribed = false;
      let checking = false;
      const unsubscribe = onUnauthorizedResponse(function () {
        // A burst of 401s from parallel requests needs one check, not one each.
        if (checking) {
          return;
        }
        checking = true;
        serverRejectsToken(watchedToken).then(function (rejected) {
          checking = false;
          if (rejected && !unsubscribed) {
            endSession();
          }
        });
      });
      return () => {
        unsubscribed = true;
        unsubscribe();
      };
    },
    [watchedToken, endSession],
  );

  // On page load, check the stored token with the server and refresh the
  // user's details. Later changes are covered by the two effects above.
  useEffect(function () {
    if (watchedToken === undefined) {
      return;
    }
    serverRejectsToken(watchedToken).then(function (rejected) {
      if (rejected) {
        endSession();
      } else {
        refreshAuthentication();
      }
    });
  }, []);
}
